/**
 * @summary - Component that Provides Stock Register Creation Page
 * @author Yuvasri S
 * @since 2024-07-26
 */
// StockRegisterCreate.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import PageTitle from '../commoncomponents/PageTitle';
import { DefaultCard } from '../../assets/styled-components/card';
import { Box, Grid } from '@mui/material';
import CustomTextFieldWithSearch from '../commoncomponents/CustomTextFieldWithSearch';
import CustomTextField from '../commoncomponents/CustomTextField';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import CustomFooter from '../commoncomponents/CustomFooter';
import { MasterGraph } from "../../utility/apiURLS";
import {
    getAuthToken,
    getcompanyId,
    getFacilityId,
    getCreatedBy,
    getModifiedBy,
} from "../../utility/tokenUtility";
import axios from "axios";
import { updateCreateStockRegister, resetCreateStockRegister } from "../../redux/reducers/StockRegisterReducer/stockSlice";
import CustomDialog from '../commoncomponents/CustomDialog';
import { useNavigate } from "react-router";

const StockRegisterCreate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const stockRegister = useSelector((state) => state.stock.stockRegister);

    const [itemCodeOptions, setItemCodeOptions] = useState([]);
    const [quantityOptions, setQuantityOptions] = useState([]);

    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const facilityId = getFacilityId("facilityId");
    const createdBy = getCreatedBy("createdBy");
    const modifiedBy = getModifiedBy("modifiedBy");

    const [errors, setErrors] = useState({
        itemCode: false,
        currentStockLevel: false,
        quantity: false
    });

    const [errorMessages, setErrorMessages] = useState({
        itemCode: "",
        currentStockLevel: "",
        quantity: ""
    });


    const [icId, setIcId] = useState('');
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [succesDialogMessage, setSuccessDialogMessage] = useState('');
    const initialStockRegisterState = {
        itemcode: '',
        itemcodeId: '',
        itemCodeDesc: '',
        itemType: '',
        service: '',
        status: '',
        workGroup: '',
        quantity: '',
        minUnit: '',
        maxUnit: '',
        minPrice: '',
        maxPrice: '',
        currentStockLevel: '',
    };
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [unsavedAddDialogOpen, setUnsavedAddDialogOpen] = useState(false);


    const fetchItemCodeOptions = async () => {
        try {
            const response = await axios.get(
                `${MasterGraph}/api/GraphStockRegister/GetStockbyFacilityid/${facilityId}/?size=1000`,
                config
            );

            const itemCodeData = response.data;
            const options = itemCodeData.map((ic) => ({
                value: ic.itemDescription,
                label: `${ic.itemcode} - ${ic.itemDescription}`,
                itemCode: ic.itemcode,
                id: ic.id,
                itemType: ic.itemType,
                itemservice: ic.itemservice,
                isactive: ic.isactive,
                workgroup: ic.workgroup,
                minimumstock: ic.minimumstock,
                maximumstock: ic.maximumstock
            }));

            setItemCodeOptions(options);
        }
        catch (error) {
            console.error("Error fetching item code:", error);
        }
    };

    const fetchQuantity = async () => {
        try {
            const response = await axios.get(
                `${MasterGraph}/api/masimsstockquantitytypes`,
                config
            );
            const quantityData = response.data;
            const options = quantityData.map((q) => ({
                value: q.quantitytype,
                label: q.quantitytype,
                id: q.id,
            }));
            setQuantityOptions(options);
       
        } catch (error) {
            console.error("Error fetching quantity options:", error);
        }
    };

    const fetchItemDetails = async (itemCode) => {
        try {
            const response = await axios.get(
                `${MasterGraph}/api/GraphStockRegister/GetStockRegisterByItemcode/${itemCode}`,
                config
            );

            const itemDataArr = response.data;

            if (itemDataArr.length > 0) {
                const itemData = itemDataArr[0];

                if (itemData.masimsstockregisters && itemData.masimsstockregisters.length > 0) {
                    const stockRegister = itemData.masimsstockregisters[0];

                    let workGroup = {
                        value: itemData.workgroup,
                        id: '',
                        label: itemData.workgroup
                    };

                    const quantityOptions = {
                        1: 'Unit',
                        2: 'Nos',
                        3: 'Kg'
                    };

                    const quantity = {
                        value: quantityOptions[stockRegister.quantity] || '',
                        id: stockRegister.quantity,
                        label: quantityOptions[stockRegister.quantity] || ''
                    };
                    handleFieldChange('quantity', quantity || '');
                    handleFieldChange('minPrice', stockRegister.minpriceperunit || '');
                    handleFieldChange('maxPrice', stockRegister.maxpriceperunit || '');
                    handleFieldChange('currentStockLevel', stockRegister.currentstocklevel || '');

                    setIcId(stockRegister.id);
                } else {
                    dispatch(updateCreateStockRegister({
                        quantity: '',
                        minpriceperunit: '',
                        maxpriceperunit: '',
                        currentstocklevel: '',
                    }));
                    setIcId('');
                }
            } else {
                dispatch(updateCreateStockRegister({
                    quantity: '',
                    minpriceperunit: '',
                    maxpriceperunit: '',
                    currentstocklevel: '',
                }));
                setIcId('');
            }
        } catch (error) {
            console.error("Error fetching item details:", error);
        }
    };

    useEffect(() => {
        fetchItemCodeOptions();
        fetchQuantity();
        if (!stockRegister.itemcode) {
            dispatch(updateCreateStockRegister(initialStockRegisterState));
        }
    }, [stockRegister.itemcode, dispatch]);

    // Reset state on mount
    useEffect(() => {
        dispatch(resetCreateStockRegister());
    }, [dispatch]);

    useEffect(() => {
        if (!stockRegister.itemcode || !stockRegister.itemcode.label) {
            dispatch(resetCreateStockRegister());
            setIcId('');
        }
    }, [stockRegister.itemcode, dispatch]);



    const handleSave = async () => {
        try {
            const payload = {
                quantity: stockRegister.quantity.id,
                masimsstockquantitytypeId: stockRegister.quantity.id,
                minpriceperunit: stockRegister.minPrice,
                maxpriceperunit: stockRegister.maxPrice,
                currentstocklevel: stockRegister.currentStockLevel,
                masimscompanyitemdetailsId: stockRegister.itemcodeId,
                createdby: createdBy,
                modifiedby: modifiedBy,
                isdeleted: false,
                id: icId
            };


            // Determine whether to make a POST or PUT request
            if (icId) {
                // PUT request
                const putUrl = `${MasterGraph}/api/masimsstockregisters/${icId}`;
                await axios.put(putUrl, payload, config);
                setSuccessDialogMessage('Data updated successfully.');
            } else {
                // POST request
                const response = await axios.post(`${MasterGraph}/api/masimsstockregisters`, payload, config);

                const newIcId = response.data.id;
                setIcId(newIcId);
                // Make the second POST request to api/masimsstockadjustments
                const adjustmentPayload = {
                    id: "",
                    createdby: createdBy,
                    modifiedby: modifiedBy,
                    isdeleted: false,
                    adjustmentdate: new Date().toISOString(),
                    physicalquantity: 0,
                    variance: 0,
                    adjustmentquantity: 0,
                    masimsstockregisterId: newIcId,
                };


                await axios.post(`${MasterGraph}/api/masimsstockadjustments`, adjustmentPayload, config);

                setSuccessDialogMessage('Data saved successfully.');
            }

            // Update state or dispatch actions as needed
            dispatch(updateCreateStockRegister({
                itemcode: stockRegister.itemcode,
                itemCodeDesc: stockRegister.itemCodeDesc,
                itemType: stockRegister.itemType,
                service: stockRegister.service,
                status: stockRegister.status,
                workGroup: stockRegister.workGroup,
                minUnit: stockRegister.minUnit,
                maxUnit: stockRegister.maxUnit,
                quantity: stockRegister.quantity,
                minPrice: stockRegister.minPrice,
                maxPrice: stockRegister.maxPrice,
                currentStockLevel: stockRegister.currentStockLevel,
            }));

            // Additional state updates if required
            setIsSubmitted(true);
            // Open success dialog
            setSuccessDialogOpen(true);

        } catch (error) {
            console.error("Error saving item code data:", error);
        }
    };


    const handleSubmit = (e) => {
        e.preventDefault();

        let formIsValid = true;
        const newErrors = {
            itemCode: false,
            currentStockLevel: false,
            quantity: false
        };
        const newErrorMessages = {
            itemCode: "",
            currentStockLevel: "",
            quantity: ""
        };
        if (!stockRegister.itemcode.label) {
            formIsValid = false;
            newErrors.itemCode = true;
            newErrorMessages.itemCode = "Item Code is required";
        }
        if (!stockRegister.currentStockLevel) {
            formIsValid = false;
            newErrors.currentStockLevel = true;
            newErrorMessages.currentStockLevel = "Current Stock Level is required";
        }
        if (!stockRegister.quantity) {
            formIsValid = false;
            newErrors.quantity = true;
            newErrorMessages.quantity = "Quantity is required";
        }
        setErrors(newErrors);
        setErrorMessages(newErrorMessages);

        if (formIsValid) {
            handleSave();
            setHasUnsavedChanges(false);
        }
    };
    const handleCancel = () => {
        const hasDataInFields = Object.values(stockRegister).some(value => value);

        if (hasUnsavedChanges && hasDataInFields) {
            setCancelDialogOpen(true);
        } else {
            dispatch(resetCreateStockRegister()); // Reset state on cancel
            setIcId('');
            navigate(-1, { replace: true });
        }
    };

    const handleAdd = () => {
        if (!isSubmitted && (stockRegister.itemcode || stockRegister.quantity || stockRegister.minPrice || stockRegister.maxPrice || stockRegister.currentStockLevel)) {
            setUnsavedAddDialogOpen(true);
        } else {
            dispatch(resetCreateStockRegister());
        }
    };

    const handleFieldChange = (field, value) => {
        setHasUnsavedChanges(true);
        dispatch(updateCreateStockRegister({ [field]: value }));
    };

    const handleItemCodeChange = (newValue) => {
        setHasUnsavedChanges(true);

        if (newValue) {
            // Trim the item code and update state
            const trimmedItemCode = (newValue ? { ...newValue, label: newValue.label.split(' ')[0].trimRight() } : '');


            dispatch(updateCreateStockRegister({
                itemcode: trimmedItemCode,
                itemcodeId: newValue.id,
                itemCodeDesc: newValue.value,
                itemType: newValue.itemType,
                service: newValue.itemservice,
                status: newValue.isactive,
                workGroup: newValue.workgroup,
                minUnit: newValue.minimumstock,
                maxUnit: newValue.maximumstock,
            }));

            if (newValue?.itemCode) {
                const processedItemCode = newValue.itemCode.includes('/')
                    ? newValue.itemCode.replace(/\//g, '%2F')
                    : newValue.itemCode;
                fetchItemDetails(processedItemCode);
            } else {
                // Handle the case where itemCode is undefined or null
                fetchItemDetails('');
            }

            setErrors({ ...errors, itemCode: false });
            setErrorMessages({ ...errorMessages, itemCode: "" });

        } else {
            // When no item code is selected, reset the stock register
            dispatch(updateCreateStockRegister(initialStockRegisterState));

            // Clear icId and any other relevant fields
            setIcId('');
            setErrors({ ...errors, itemCode: true });
            setErrorMessages({ ...errorMessages, itemCode: "Item Code is required" });
        }
    };




    const handleQuantityChange = (newValue) => {
        handleFieldChange('quantity', newValue);
        if (newValue) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                quantity: false,
            }));
            setErrorMessages((prevMessages) => ({
                ...prevMessages,
                quantity: "",
            }));
        }
    };

    const handleMinPriceChange = (newValue) => {
        const numericValue = newValue.replace(/[^0-9]/g, '');

        handleFieldChange('minPrice', numericValue);
    };

    const handleMaxPriceChange = (newValue) => {
        const numericValue = newValue.replace(/[^0-9]/g, '');

        handleFieldChange('maxPrice', numericValue);
    };

    const handleCurrentStockLevelChange = (newValue) => {
        const numericValue = newValue.replace(/[^0-9]/g, '');
        handleFieldChange('currentStockLevel', numericValue);
        if (numericValue) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                currentStockLevel: false,
            }));
            setErrorMessages((prevMessages) => ({
                ...prevMessages,
                currentStockLevel: "",
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                currentStockLevel: true,
            }));
            setErrorMessages((prevMessages) => ({
                ...prevMessages,
                currentStockLevel: "Current Stock Level is required",
            }));
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };
    const handleUnsavedAddConfirm = () => {
        dispatch(resetCreateStockRegister());
        setUnsavedAddDialogOpen(false);
    };
    const handleUnsavedAddDialogClose = () => {
        setUnsavedAddDialogOpen(false);
    };

    return (
        <>
            <PageTitle
                title={"Create Stock Register"}
            />

            <DefaultCard sx={{ height: "100%" }}>
                <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                    <Box mb={2}>
                        <Grid container gap={3}>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextFieldWithSearch
                                    maxlength={16}
                                    label="Item Code"
                                    id="item-code"
                                    value={stockRegister.itemcode}
                                    options={itemCodeOptions}
                                    onChange={handleItemCodeChange}
                                    isMandatoryField={true}
                                    error={errors.itemCode}
                                    errorMessage={errorMessages.itemCode}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={120}
                                    label="Item Code Description"
                                    id="item-code-desc"
                                    value={stockRegister.itemCodeDesc}
                                    disabled={true}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={2}
                                    label="Item Type"
                                    id="item-type"
                                    value={stockRegister.itemType}
                                    disabled={true}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={20}
                                    label="Service"
                                    id="service"
                                    value={stockRegister.service}
                                    disabled={true}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={5}
                                    label="Status"
                                    id="status"
                                    value={stockRegister.status}
                                    disabled={true}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    //maxlength={5}
                                    label="Work Group"
                                    id="work-group"
                                    value={stockRegister.workGroup}
                                    disabled={true}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomSelectInput
                                    id="quantity"
                                    label="Quantity"
                                    value={stockRegister.quantity}
                                    options={quantityOptions}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    onChange={handleQuantityChange}
                                    isMandatoryField={true}
                                    error={errors.quantity}
                                    errorMessage={errorMessages.quantity}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Min Unit"
                                    id="min-unit"
                                    value={stockRegister.minUnit}
                                    disabled={true}
                                    placeholder=""
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Max Unit"
                                    id="max-unit"
                                    value={stockRegister.maxUnit}
                                    placeholder=""
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={40}
                                    label="Min Price per unit(RM)"
                                    id="min-price"
                                    placeholder={"Enter Number"}
                                    value={stockRegister.minPrice}
                                    onChange={(e) => handleMinPriceChange(e)}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={40}
                                    label="Max Price per unit(RM)"
                                    id="max-price"
                                    placeholder={"Enter Number"}
                                    value={stockRegister.maxPrice}
                                    onChange={(e) => handleMaxPriceChange(e)}
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={10}
                                    label="Current Stock Level"
                                    id="stock-level"
                                    placeholder={"Enter Number"}
                                    value={stockRegister.currentStockLevel}
                                    onChange={(e) => handleCurrentStockLevelChange(e)}
                                    isMandatoryField={true}
                                    error={errors.currentStockLevel}
                                    errorMessage={errorMessages.currentStockLevel}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </form>
                <CustomFooter
                    cancelClick={handleCancel}
                    saveClick={handleSubmit}
                    addClick={handleAdd}
                    showAddButton={true}
                    buttonLabel={"Save"}
                />
                <CustomDialog
                    open={successDialogOpen}
                    onClose={() => setSuccessDialogOpen(false)}
                    content={succesDialogMessage}
                    confirmBtn="OK"
                    onConfirm={() => setSuccessDialogOpen(false)}
                />
                <CustomDialog
                    open={cancelDialogOpen}
                    onClose={handleCancelDialogClose}
                    content="Record(s) not Saved/Updated. Do you want to continue?"
                    confirmBtn="Yes"
                    cancelBtn="No"
                    onConfirm={handleCancelDialogConfirm}
                    onCancel={handleCancelDialogClose}
                />
                <CustomDialog
                    open={unsavedAddDialogOpen}
                    content="Record(s) not Saved/Updated. Do you want to continue?"
                    onConfirm={handleUnsavedAddConfirm}
                    onClose={handleUnsavedAddDialogClose}
                    cancelBtn="No"
                    confirmBtn="Yes"
                />
            </DefaultCard>
        </>
    );
};
export default StockRegisterCreate;
